import React, { useEffect } from 'react';
import { 
  Monitor, Code, Smartphone, Brain, Globe, TrendingUp, Layout, Cloud, CheckCircle2, Zap, Rocket, Shield 
} from 'lucide-react';
import SEO from '../seo/SEO';

const ITSolutionsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreServices = [
    { title: "Digital Marketing", icon: TrendingUp, desc: "Boost your online presence with data-driven SEO, SEM, and social media strategies." },
    { title: "Website Development", icon: Globe, desc: "Craft stunning, responsive websites tailored to your specific brand objectives." },
    { title: "App Development", icon: Smartphone, desc: "Build powerful mobile applications for iOS and Android with seamless UX." },
    { title: "Software Development", icon: Code, desc: "Custom software solutions designed to streamline operations and enhance productivity." },
    { title: "AI Solutions", icon: Brain, desc: "Integrate intelligent Machine Learning to automate processes and gain insights." },
    { title: "Cloud Services", icon: Cloud, desc: "Scalable cloud infrastructure for enhanced flexibility and secure data management." }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <SEO
        title="IT Solutions & Technology Services | GlobalEPRC"
        description="GlobalEPRC delivers digital marketing, website & app development, custom software, AI solutions and cloud services to help your business lead its industry."
        path="/it-solutions"
      />

      {/* 1. HERO SECTION */}
      <section className="pt-48 pb-24 bg-slate-900 relative overflow-hidden text-white">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gold-gradient opacity-10 blur-[120px]"></div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2">
              <div className="inline-flex items-center gap-2 bg-white/10 text-amber-400 px-4 py-1.5 rounded-full mb-8 backdrop-blur-md border border-white/10 font-bold text-xs uppercase tracking-[0.2em]">
                <Zap className="h-4 w-4" /> Next-Gen Technology
              </div>
              <h1 className="text-5xl md:text-7xl font-black mb-8 leading-tight">
                Digital <span className="text-gold-gradient">Innovation</span> & IT Solutions
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                GlobalEPRC bridges the gap between complex business challenges and high-tech solutions. We deliver the digital tools you need to lead your industry.
              </p>
              <div className="flex flex-wrap gap-4 text-sm font-semibold text-amber-100">
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500" /> AI-Driven Insights</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500" /> Enterprise Mobility</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500" /> Custom Architecture</span>
              </div>
            </div>
            <div className="lg:w-1/2">
               <div className="relative group">
                 <div className="absolute -inset-4 bg-gold-gradient rounded-[3rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                 <img 
                   src="https://img.freepik.com/premium-photo/it-specialist-discussing-project-details-with-colleagues_1297251-15618.jpg?w=1480" 
                   alt="Cyber Technology" 
                   className="relative rounded-[2.5rem] shadow-2xl border border-white/10 object-cover h-[450px] w-full"
                 />
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CORE SERVICE OFFERINGS GRID */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6">Our Technology Spectrum</h2>
            <div className="h-1.5 w-24 bg-gold-gradient mx-auto rounded-full mb-8"></div>
            <p className="text-lg text-slate-600 leading-relaxed">
              From startups to global enterprises, we provide the technical foundation required to scale operations and dominate the digital landscape.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreServices.map((item, i) => (
              <div key={i} className="group p-10 rounded-[2.5rem] bg-white border border-slate-100 hover:shadow-2xl transition-all duration-500">
                <div className="bg-amber-50 p-5 rounded-2xl group-hover:bg-gold-gradient transition-all w-fit mb-8">
                  <item.icon className="h-8 w-8 text-amber-600 group-hover:text-white" />
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. DETAILED SERVICES SECTION */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          
          {/* Digital Marketing */}
          <div className="flex flex-col lg:flex-row gap-20 items-center mb-32">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=400" className="rounded-2xl shadow-lg mt-10 h-64 object-cover" alt="Marketing Data" />
                <img src="https://img.freepik.com/free-photo/businessman-holding-digital-tablet-with-digital-marketing-graph_9975-22063.jpg?t=st=1771915790~exp=1771919390~hmac=8566b36f50cc5c7614e164d23c91f5b26a7de9b245133d1da5a3e7556189c7e2&w=1480" className="rounded-2xl shadow-lg h-64 object-cover" alt="Growth Charts" />
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2">
              <h3 className="text-amber-600 font-bold uppercase tracking-[0.2em] text-sm mb-4">Strategic Visibility</h3>
              <h2 className="text-4xl font-black text-slate-900 mb-6">Digital Marketing</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We don't just generate clicks; we drive conversions. By combining SEO, targeted PPC, and high-impact social media management, we ensure your brand remains at the forefront of your customer's minds.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {['Advanced SEO/SEM', 'Social Commerce', 'Content Architecture', 'PPC Maximization'].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100 text-sm font-bold">
                    <TrendingUp className="h-4 w-4 text-amber-600" /> {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Web & App Development */}
          <div className="flex flex-col lg:flex-row gap-20 items-center mb-32">
            <div className="lg:w-1/2">
              <h3 className="text-amber-600 font-bold uppercase tracking-[0.2em] text-sm mb-4">Core Infrastructure</h3>
              <h2 className="text-4xl font-black text-slate-900 mb-6">Web & App Ecosystems</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Our development team specializes in creating high-performance digital products. From reactive web platforms to native mobile apps, we focus on speed, security, and intuitive user journeys.
              </p>
              <div className="p-8 bg-slate-900 rounded-[2rem] border border-slate-800 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Smartphone className="h-24 w-24 text-white" />
                </div>
                <h4 className="font-bold text-amber-400 mb-4 flex items-center gap-2"><Rocket className="h-5 w-5" /> Agile Deployment</h4>
                <p className="text-sm text-slate-300 leading-relaxed">
                  We use modern frameworks like React, Flutter, and Node.js to ensure your applications are scalable from day one.
                </p>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80&w=800" className="rounded-[3rem] shadow-2xl border border-slate-100" alt="Programming Code" />
            </div>
          </div>

          {/* AI & Software Solutions */}
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-6 bg-gold-gradient opacity-10 blur-3xl rounded-full"></div>
                <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800" className="rounded-[3rem] relative z-10 shadow-2xl" alt="AI Interface" />
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2">
              <h3 className="text-amber-600 font-bold uppercase tracking-[0.2em] text-sm mb-4">Future Readiness</h3>
              <h2 className="text-4xl font-black text-slate-900 mb-6">AI & Custom Software</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Harness the power of Artificial Intelligence to automate complex workflows and predict market trends. Our bespoke software solutions are engineered to solve your unique business bottlenecks.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                  <Brain className="text-amber-600 h-6 w-6" />
                  <div>
                    <p className="font-bold text-slate-900">Neural Automation</p>
                    <p className="text-xs text-slate-600">Automate repetitive tasks with 99% accuracy.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <Shield className="text-amber-600 h-6 w-6" />
                  <div>
                    <p className="font-bold text-slate-900">Enterprise Security</p>
                    <p className="text-xs text-slate-600">Built-in military-grade encryption for all custom tools.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 4. CALL TO ACTION */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-8">Ready to Build Your Digital Future?</h2>
          <button className="bg-gold-gradient text-white px-10 py-4 rounded-full font-black text-lg hover:scale-105 transition-transform shadow-lg">
            Schedule a Consultation
          </button>
        </div>
      </section>
    </div>
  );
};

export default ITSolutionsPage;